import React, { useEffect, useState } from 'react';
import { Collapse, Input, Table } from 'reactstrap';
import moment from 'moment';
import FormCard from '../../shared/components/custom/FormCard';
import Loading from '../../shared/components/custom/Loading';
import Api from '../../util/api';
import useConfig from '../../util/useConfig';
import { partial } from '../../util/translation/translation';
import { exportSheetsToExcelFile } from '../../util/xlsx';
import DisplayCounter from '../_uiComponents/DisplayCounter';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import VoteOutlineIcon from 'mdi-react/VoteOutlineIcon';
import StadiumIcon from 'mdi-react/StadiumIcon';
import DownloadOutlineIcon from 'mdi-react/DownloadOutlineIcon';
import HelpCircleOutlineIcon from 'mdi-react/HelpCircleOutlineIcon';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import InfoBox from '../../shared/components/custom/InfoBox';

const p = partial('mvpLeaderboard');

const playerName = (player) =>
  player
    ? [player.firstname, player.lastname].filter(Boolean).join(' ') || '-'
    : p('unknownPlayer');

const initials = (player) =>
  [player?.firstname, player?.lastname]
    .filter(Boolean)
    .map((name) => name[0].toUpperCase())
    .join('') || '?';

const seasonLabel = (season) =>
  season
    ? `${season.year}${season.type === 'half' ? ` (${p('halfSeason')})` : ''}`
    : '';

// The season's MVP standings from the fans' match votes, per team
const MvpLeaderboard = () => {
  const config = useConfig();
  const teams = config.teams || [];
  const [teamId, setTeamId] = useState(teams[0]?.id ?? null);
  const [seasonId, setSeasonId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  useEffect(() => {
    if (!teamId) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    Api.matchCenter
      .getMvpLeaderboard(teamId, seasonId)
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [teamId, seasonId]);

  const team = teams.find((item) => item.id === teamId);
  const exportToExcel = () =>
    exportSheetsToExcelFile(
      [
        {
          name: p('title'),
          rows: data.players.map((row) => ({
            [p('rank')]: row.rank,
            [p('player')]: playerName(row.player),
            [p('jersey')]: row.player?.jerseyNumber ?? '',
            [p('mvpWins')]: row.mvpWins,
            [p('points')]: row.points,
            [p('votes')]: row.votes,
            [p('fans')]: row.voters,
            [p('matches')]: row.matches,
          })),
        },
      ],
      `${p('fileName')}-${team?.name || teamId}-${data.season?.year || ''}`
    );

  const renderRank = (rank) =>
    rank <= 3 ? (
      <span className={`mvp-rank mvp-rank--${rank}`}>{rank}</span>
    ) : (
      <span className="mvp-rank">{rank}</span>
    );

  const renderMessage = (text, className = 'text-muted') => (
    <div className={`mvp-board__message ${className}`}>{text}</div>
  );

  return (
    <FormCard
      title={p('title')}
      className="mb-3"
      bodyClassName="form-card__body--flush"
      actions={
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <SquareIconButton
            icon={<HelpCircleOutlineIcon />}
            title={p('howTitle')}
            id="mvp-how-to"
            active={showHowTo}
            onClick={() => setShowHowTo((open) => !open)}
          />
          <SquareIconButton
            icon={<DownloadOutlineIcon />}
            title={p('export')}
            id="mvp-export"
            disabled={!data?.players?.length}
            onClick={exportToExcel}
          />
        </div>
      }
    >
      <Loading loading={loading} />
      <div className="mvp-board__top">
        <Collapse isOpen={showHowTo}>
          <InfoBox className="mb-3">
            <strong className="d-block mb-1">{p('howTitle')}</strong>
            <ol className="mvp-board__how">
              <li>{p('howVoting')}</li>
              <li>{p('howMatch')}</li>
              <li>{p('howSeason')}</li>
              <li>{p('howRanking')}</li>
            </ol>
            <div className="text-muted">{p('howExample')}</div>
            <div className="text-muted mt-1">{p('howScope')}</div>
          </InfoBox>
        </Collapse>
        <div className="mvp-board__filters">
          {teams.length > 1 && (
            <label className="mvp-board__filter">
              <span className="form-header">{p('team')}</span>
              <Input
                type="select"
                value={teamId ?? ''}
                onChange={(e) => {
                  setTeamId(e.target.value);
                  setSeasonId(null);
                }}
              >
                {teams.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name || item.shortName || item.id}
                  </option>
                ))}
              </Input>
            </label>
          )}
          <label className="mvp-board__filter">
            <span className="form-header">{p('season')}</span>
            <Input
              type="select"
              value={data?.season?.id ?? ''}
              disabled={!data?.seasons?.length}
              onChange={(e) => setSeasonId(e.target.value)}
            >
              {(data?.seasons || []).map((season) => (
                <option key={season.id} value={season.id}>
                  {seasonLabel(season)}
                </option>
              ))}
            </Input>
          </label>
          {data?.season && (
            <span className="mvp-board__dates">
              {p('seasonDates', {
                from: moment(data.season.seasonStart).format('D MMM YYYY'),
                to: moment(data.season.seasonEnd).format('D MMM YYYY'),
              })}
            </span>
          )}
        </div>
        {!failed && data?.season && (
          <div className="mvp-board__counters">
            <DisplayCounter
              id="mvp-votes"
              title={p('totalVotes')}
              text={p('totalVotesInfo')}
              value={Number(data.votes || 0).toLocaleString()}
              icon={<VoteOutlineIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="mvp-voters"
              title={p('uniqueFans')}
              text={p('uniqueFansInfo')}
              value={Number(data.voters || 0).toLocaleString()}
              icon={<AccountMultipleIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="mvp-matches"
              title={p('matchesWithVotes')}
              text={p('matchesWithVotesInfo')}
              value={Number(data.matches || 0).toLocaleString()}
              icon={<StadiumIcon style={{ width: 18 }} />}
            />
          </div>
        )}
      </div>
      {failed && renderMessage(p('loadError'), 'text-danger')}
      {!failed &&
        data &&
        data.players.length === 0 &&
        renderMessage(p('noVotes'))}
      {!failed && data?.players?.length > 0 && (
        <Table
          responsive
          striped
          className="table--roomy table--stack mb-0 mvp-table"
        >
          <thead>
            <tr>
              <th className="mvp-table__rank">{p('rank')}</th>
              <th>{p('player')}</th>
              <th className="mvp-table__num" title={p('pointsInfo')}>
                {p('points')}
              </th>
              <th className="mvp-table__num" title={p('mvpWinsInfo')}>
                {p('mvpWins')}
              </th>
              <th className="mvp-table__num">{p('votes')}</th>
              <th className="mvp-table__num" title={p('fansInfo')}>
                {p('fans')}
              </th>
              <th className="mvp-table__num">{p('matches')}</th>
            </tr>
          </thead>
          <tbody>
            {data.players.map((row) => (
              <tr key={row.playerId}>
                <td className="mvp-table__rank" data-label={p('rank')}>
                  {renderRank(row.rank)}
                </td>
                <td className="table-stack__primary">
                  <div className="mvp-player">
                    {row.player?.imageUrl ? (
                      <img
                        className="mvp-player__avatar"
                        src={row.player.imageUrl}
                        alt=""
                      />
                    ) : (
                      <span className="mvp-player__avatar" aria-hidden>
                        {initials(row.player)}
                      </span>
                    )}
                    <div className="mvp-player__text">
                      <span className="mvp-player__name">
                        {playerName(row.player)}
                        {row.player?.jerseyNumber != null && (
                          <span className="mvp-player__jersey">
                            #{row.player.jerseyNumber}
                          </span>
                        )}
                      </span>
                      {row.player?.position && (
                        <small className="text-muted">
                          {row.player.position}
                        </small>
                      )}
                    </div>
                  </div>
                </td>
                {/* Points decide the ranking, so they lead the numbers. */}
                <td className="mvp-table__num" data-label={p('points')}>
                  <strong>{row.points}</strong>
                </td>
                <td className="mvp-table__num" data-label={p('mvpWins')}>
                  {row.mvpWins}
                </td>
                <td className="mvp-table__num" data-label={p('votes')}>
                  {row.votes}
                </td>
                <td className="mvp-table__num" data-label={p('fans')}>
                  {row.voters}
                </td>
                <td className="mvp-table__num" data-label={p('matches')}>
                  {row.matches}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </FormCard>
  );
};

export default MvpLeaderboard;
